import { useMsal } from "@azure/msal-react";
import { useSearchParams } from "react-router-dom"
import React, { useMemo, useState } from "react";
import { generateShareLink } from "../api/linearClient";
import { internalApiScopes } from "../msalConfig";
import rootStyles from "../styles/internalRoot.module.scss";
import styles from "../styles/internalShareIssuePage.module.scss"

export default function InternalSharePage() {
	const { instance } = useMsal();
	const [searchParams] = useSearchParams();
	const prefillId = searchParams.get("id");
	const scopes = useMemo(() => internalApiScopes, []);
	const [issueId, setIssueId] = useState(prefillId ?? "");
	const [expiresAt, setExpiresAt] = useState("");
	const [url, setUrl] = useState<string | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [loading, setLoading] = useState(false);
	const [copied, setCopied] = useState(false);


	async function handleSubmit(e: React.FormEvent) {
		e.preventDefault();
		setError(null);
		setUrl(null);
		const trimmed = issueId.trim();
		if (!trimmed) {
			setError("Linear issue ID is required");
			return;
		}
		setLoading(true);
		try {
			const { url: generatedUrl } = await generateShareLink(instance, scopes, {
				issueId: trimmed,
				expiresAt: expiresAt.trim() || undefined,
			});
			setUrl(generatedUrl);
		} catch (e: any) {
			setError(e?.message || String(e));
		} finally {
			setLoading(false);
		}
	}

	async function handleCopy() {
		if (!url) return;
		try {
			await navigator.clipboard.writeText(url);
			setCopied(true);
			setTimeout(() => setCopied(false), 2000);
		} catch {
			setError("Failed to copy to clipboard");
		}
	}

	return (
		<div className={rootStyles.page} style={{ display: "flex" }}>
			<div className={rootStyles.content}>
				<h1 className={rootStyles.h1}>Share Linear issue (public link)</h1>
				<p className={rootStyles.muted} style={{ marginBottom: 16 }}>
					Generate a signed link so anyone with the link can view a single
					Linear issue without signing in. Optional: set an expiration date.
				</p>
				{error ? <div className={rootStyles.error}>{error}</div> : null}
				<form onSubmit={handleSubmit} id={styles.form}>
					<div style={{ flexDirection: "column", flex: 1 }}>
						<div style={{ marginBottom: 12 }}>
							<label
								htmlFor="share-issue-id"
								style={{
									display: "block",
									marginBottom: 4,
									fontSize: 13,
									color: "var(--text-muted)",
								}}
							>
								Linear issue identifier
							</label>
							<input
								id="share-issue-id"
								type="text"
								value={issueId}
								onChange={(e) => setIssueId(e.target.value)}
								placeholder="e.g. TICK-42 or full UUID"
								disabled={loading}
								style={{
									width: "100%",
									maxWidth: 400,
									padding: "8px 12px",
									fontSize: 14,
									background: "var(--bg-surface)",
									border: "1px solid var(--border-color)",
									borderRadius: 8,
									color: "var(--text-primary)",
								}}
							/>
						</div>
						<div style={{ marginBottom: 16 }}>
							<label
								htmlFor="share-expires"
								style={{
									display: "block",
									marginBottom: 4,
									fontSize: 13,
									color: "var(--text-muted)",
								}}
							>
								Expiration (optional, ISO date)
							</label>
							<div style={{ position: "relative", maxWidth: 400 }}>
								<input
									id="share-expires"
									type="datetime-local"
									value={expiresAt}
									onChange={(e) => setExpiresAt(e.target.value)}
									disabled={loading}
									style={{
										width: "100%",
										padding: "8px 12px",
										fontSize: 14,
										background: "var(--bg-surface)",
										border: "1px solid var(--border-color)",
										borderRadius: 8,
										color: "var(--text-primary)",
									}}
								/>
								<div
									onClick={() => (document.getElementById("share-expires") as HTMLInputElement)?.showPicker()}
									style={{
										position: "absolute",
										top: 0,
										right: 0,
										width: "40%",
										height: "100%",
										cursor: "pointer",
									}}
								/>
							</div>
						</div>
					</div>
					<div style={{ display: "flex", flex: 1, justifyContent: "center", alignItems: "center" }}>
						<button
							type="submit"
							disabled={loading}
							className={styles.button}
							style={{
								cursor: loading ? "not-allowed" : "pointer",
							}}
						>
							{loading ? "Generating…" : "Generate link"}
						</button>
					</div>
				</form>
				{
					url ? (
						<div>
							<div
								style={{
									marginBottom: 4,
									fontSize: 13,
									color: "var(--text-muted)",
								}}
							>
								Share link (copy and share)
							</div>
							<div
								style={{
									display: "flex",
									alignItems: "center",
									gap: 8,
									flexWrap: "wrap",
								}}
							>
								<input
									type="text"
									readOnly
									value={url}
									style={{
										flex: "1",
										minWidth: 200,
										padding: "8px 12px",
										fontSize: 13,
										background: "var(--bg-surface)",
										border: "1px solid var(--border-color)",
										borderRadius: 8,
										color: "var(--text-primary)",
									}}
								/>
								<button
									type="button"
									onClick={handleCopy}
									style={{
										padding: "8px 16px",
										fontSize: 13,
										border: "1px solid var(--border-color)",
										cursor: "pointer",
									}}
								>
									{copied ? "Copied" : "Copy"}
								</button>
							</div>
						</div>
					) : null
				}
			</div >
		</div >
	);
}
